import { Injectable } from '@angular/core';
import { DropdownListingInterface } from '@app/finxone-web-frontend/app/Modules/shared/widgets/accounts-context-dropdown-widget/accounts-context-dropdown-widget.component';
import {
  AccountInfoType,
  AccountStatusType,
  UiZoneWidgetAttributeConfig,
  isMultiCurrencyAccountInfoType,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { GetAccount, UpdateCurrentAccountId } from '../../../actions/account.action';
import { FetchTotalWealth } from '../../../actions/total-wealth.action';
import { Currency } from '../../../dtos/currencies.dto';
import { AccountState, AccountStateModel } from '../../../state/account.state';
import { resolveAccountIdentifierFields } from '../../../utils/account-identifier.utils';
import { getCurrencyDataByKey } from '../../../utils/utils';
import { AccountListingStrategyInterface } from '../../interfaces/account-listing-strategy.type';

@Injectable()
export class CurrentAccountsStrategy implements AccountListingStrategyInterface {
  @Select(AccountState.getAccounts)
  private accountState$: Observable<AccountStateModel>;
  private accountState: AccountStateModel;
  private accounts: AccountInfoType[];
  private selectedAccount: DropdownListingInterface;
  private dropdownListing: DropdownListingInterface[];

  public setupStateSubscription(
    store: Store,
    pageNumber: number,
    pageSize: number,
    accountContextStatusList: AccountStatusType[],
  ): Observable<any> {
    store.dispatch(new GetAccount(pageNumber, pageSize, accountContextStatusList));

    return this.getAccountsStream();
  }

  public getAccountsStream(): Observable<any> {
    return this.accountState$;
  }

  public getAccounts(response: AccountStateModel) {
    this.accountState = response;
    this.accounts = response.accounts;
    return response.accounts;
  }

  public getDropdownListing(response: AccountStateModel, _: Store, currencies?: Currency[]) {
    this.dropdownListing = [];
    const listOfAccounts: DropdownListingInterface[] = [];
    response.accounts.forEach((element: AccountInfoType, index: number) => {
      const account = this.getAccountData(element, index, currencies);
      listOfAccounts.push(account);
    });

    this.dropdownListing = listOfAccounts;
    return listOfAccounts;
  }

  public getSelectedAccountValueForDropdown(store: Store): DropdownListingInterface {
    const currentAccountDetails = store.selectSnapshot(
      AccountState.getCurrentAccountDetails(this.accountState.currentAccountId),
    );

    if (currentAccountDetails) {
      this.selectedAccount = this.getAccountData(currentAccountDetails, 0);
    }
    return this.selectedAccount;
  }

  public getCurrentMultiCurrencyCode(store: Store) {
    return store.selectSnapshot(AccountState.getCurrentMultiCurrencyCode);
  }

  public selectAccount(
    account: DropdownListingInterface,
    store: Store,
    attri: UiZoneWidgetAttributeConfig,
    enableTotalWealth?: boolean,
  ): void {
    this.selectedAccount = account;
    store.dispatch(new UpdateCurrentAccountId(account.accountId));

    if (enableTotalWealth && account) {
      const totalWealthDefaultCurrencyOption = attri?.['totalWealthDefaultCurrencyOption'];
      const totalWealthCustomCurrency = attri?.['totalWealthCustomCurrency'];
      this.getTotalWealthData(
        store,
        enableTotalWealth,
        totalWealthDefaultCurrencyOption,
        totalWealthCustomCurrency,
      );
    }
  }

  private getTotalWealthData(
    store: Store,
    enableTotalWealth: boolean,
    totalWealthDefaultCurrencyOption: string,
    totalWealthCustomCurrency: string,
  ) {
    if (totalWealthDefaultCurrencyOption === 'custom-currency') {
      store.dispatch(new FetchTotalWealth(totalWealthCustomCurrency, enableTotalWealth));
    } else if (this.selectedAccount) {
      const accountInfo = this.selectedAccount as unknown as AccountInfoType;
      if (accountInfo && !isMultiCurrencyAccountInfoType(accountInfo)) {
        store.dispatch(new FetchTotalWealth(accountInfo?.currency, enableTotalWealth));
      }
    }
  }

  public fetchNewAccounts(
    store: Store,
    pageNumber: number,
    pageSize: number,
    accountContextStatusList: AccountStatusType[],
  ): void {
    store.dispatch(new GetAccount(pageNumber, pageSize, accountContextStatusList));
  }

  public clearData(): void {
    this.accountState = {} as AccountStateModel;
    this.accounts = [];
    this.selectedAccount = {} as DropdownListingInterface;
    this.dropdownListing = [];
  }

  public requiresFullAccountList(): boolean {
    return false;
  }

  private getAccountData(
    accountInfo: AccountInfoType,
    index: number,
    currencies?: Currency[],
  ): DropdownListingInterface {
    // Listing renders every account, so there is no single selected currency to
    // scope to here — resolve each identifier field across all identifiers.
    const identifierFields = resolveAccountIdentifierFields(accountInfo, undefined);
    return {
      ...accountInfo,
      accountCategoryType: accountInfo.accountType,
      currencyCode: accountInfo.currency,
      accountBalance: accountInfo?.balance ?? '',
      totalBalance: accountInfo?.indicativeBalance,
      sortCode: identifierFields.sortCode,
      currencyFullName:
        currencies?.find((currency: Currency) => currency.currency_code === accountInfo?.currency)
          ?.currency_name ?? 'Unknown Currency',
      alias: accountInfo?.alias ?? '',
      accountNumber: identifierFields.accountNumber,
      bic: identifierFields.bic,
      ledgerNumber: identifierFields.ledgerNumber,
      iban: identifierFields.iban,
      providerExtraInfo: identifierFields.providerExtraInfo,
      achAccountNumber: identifierFields.achAccountNumber,
      achNumber: identifierFields.achNumber,
      abaAccountNumber: identifierFields.abaAccountNumber,
      abaNumber: identifierFields.abaNumber,
      accountProviderExtraInfo: identifierFields.providerExtraInfo,
      pendingExchangeNetTotal: accountInfo?.scheduledExchanges?.netTotal ?? '',
      pendingExchangeCount: accountInfo?.scheduledExchanges?.totalBooked ?? '',
      pendingTransactionNetTotal: accountInfo?.scheduledPayments?.netTotal ?? '',
      pendingTransactionCount: accountInfo?.scheduledPayments?.totalBooked ?? '',
      defaultMarker: index === 0 ? '(Default)' : '',
      currencyName: getCurrencyDataByKey('currency_name', accountInfo, currencies ?? []),
      countryCode: accountInfo.country,
      accountStatus: `${accountInfo?.status?.charAt(0)?.toUpperCase()}${accountInfo?.status?.slice(1)}`,
    };
  }
}
